import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {IFrameMessageService} from './services/iframe/iframe-message.service';
import {BaseSiteService} from '@spartacus/core';
import {DynamicHeadingService} from './layout/header/dynamic-heading/dynamic-heading.service';
import {ScriptInjectionService} from './config/script-injection.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {GoogleAutocompleteService} from './services/google-autocomplete.service';
import {ProductDetailsPageHistoryService} from './services/user-registration/product-details-page-history.service';
import {DcpBaselineAuthService} from '@app/auth/services/dcp-baseline-auth.service';
import {CLOSE_EVENT, FeatureTogglesService, STORE_LOADED_EVENT, STORE_RESIZE_EVENT} from '@dcp/configurator';
import {UNLEASH_TOGGLES} from './config/FeatureToggleConfig';

@UntilDestroy()
@Component({
  selector: 'dcp-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  title = 'dcp-commerce-ui';
  modalMode: boolean = false;
  modalIsSlim: boolean = false;
  modalHeader: string;
  private modalViewFeatureToggle: boolean;
  @ViewChild('wrapper') private wrapper!: ElementRef;

  resizeObserver: null | ResizeObserver;

  constructor(
    public baseSiteService: BaseSiteService,
    public changeDetectorRef: ChangeDetectorRef,
    public dynamicHeadingService: DynamicHeadingService,
    private messageService: IFrameMessageService,
    private scriptInjectionService: ScriptInjectionService,
    public googleAutocompleteService: GoogleAutocompleteService,
    public productDetailsPageHistoryService: ProductDetailsPageHistoryService,
    private featureTogglesService: FeatureTogglesService,
    public dcpBaselineAuthService: DcpBaselineAuthService
  ) {
  }

  @HostListener('click')
  closeModal() {
    if (this.modalViewFeatureToggle && this.modalMode) {
      this.messageService.postMessage({
        eventId: CLOSE_EVENT,
      });
    }
  }

  ngOnInit(): void {
    this.scriptInjectionService.injectExternalScripts();

    this.featureTogglesService.isEnabled(UNLEASH_TOGGLES.modalView)
      .pipe(untilDestroyed(this))
      .subscribe(enabled => {
          this.modalViewFeatureToggle = enabled;
          if (this.modalViewFeatureToggle) {
            this.handleToModalProperties();
          }
        }
      );
  }

  ngAfterViewInit(): void {
    this.setUpResizeObserver();
    this.messageService.postMessage({
      eventId: STORE_LOADED_EVENT,
    });
  }

  setUpResizeObserver(): void {
    if (this.messageService.isIframe()) {
      this.resizeObserver = new ResizeObserver((_) => {
        this.onResize();
      });
      this.resizeObserver.observe(this.wrapper.nativeElement);
    }
  }

  onResize() {
    const element = this.wrapper.nativeElement;
    const {offsetHeight, scrollHeight, offsetWidth, scrollWidth} = element;
    this.messageService.postMessage({
      eventId: STORE_RESIZE_EVENT,
      details: {
        offsetHeight,
        scrollHeight,
        offsetWidth,
        scrollWidth,
      },
    });
  }

  ngOnDestroy(): void {
    if (this.messageService.isIframe()) {
      this.resizeObserver.unobserve(this.wrapper.nativeElement);
    }
  }

  /**
   * The entire DCP page might be displayed as a modal.
   * This is used in SAP4Me, when the configurator is opened.
   */
  handleToModalProperties() {
    this.messageService.getActiveModalMode().pipe(untilDestroyed(this)).subscribe(mode => {
      this.modalIsSlim = mode === 'slimModal';
      this.modalMode = this.modalIsSlim || mode === 'modal';
    });
    this.dynamicHeadingService.getHeading$().pipe(untilDestroyed(this)).subscribe((value) => {
      this.modalHeader = value;
      this.changeDetectorRef.detectChanges();
    });
  }

  isModalMode(): boolean {
    return this.modalMode && this.modalViewFeatureToggle;
  }

  isSlimMode(): boolean {
    return this.isModalMode() && this.modalIsSlim;
  }

  /**
   * Click event stop propagation was added in https://github.tools.sap/DCP/dcp-commerce-ui/pull/665/files#diff-8d554c98f4c32fee762d6a18e8e189f8898124312ba390bdc51fadd38345674fR1
   * This makes contextual navigation stop working properly - so stopping propagation for
   * modal mode only if not clicking the cx navigation.
   *
   * @param event
   */
  stopClickPropagation(event: Event) {
    if (event.target !== document.querySelector('ds-contextual-navigation') && this.isModalMode()) {
      event.stopPropagation();
    }
  }
}
